import type { RequestFn } from '../http';

export type BillingJournalKind = 'WORK' | 'TRAVEL_TIME' | 'TRAVEL_DISTANCE' | 'MATERIAL';
export type BillingJournalView = 'all' | 'waiting' | 'failed' | 'manual' | 'cancelled';
export type BillingJournalSyncStatus = 'PENDING' | 'SYNCED' | 'FAILED' | 'NOT_REQUIRED';

export interface BillingJournalEntry {
  id: string;
  kind: BillingJournalKind;
  /** COCKPIT | KIAN | ONCALL | PORTAL | SERVICE_REPORT | DELIVERY | DELIVERY_ORDER */
  source: string;
  performedAt: string;
  endedAt: string | null;
  customerId: string | null;
  customerName: string | null;
  weclappCustomerId: string | null;
  weclappTicketId: string | null;
  ticketNumber: string | null;
  cwTicketId: number | null;
  documentNumber: string | null;
  submissionId: string | null;
  technicianName: string | null;
  description: string | null;
  actualMinutes: number | null;
  bookedMinutes: number | null;
  bookedBillableMinutes: number | null;
  articleId: string | null;
  articleName: string | null;
  articleNumber: string | null;
  quantity: number | null;
  serialNumbers: string[];
  distanceKm: number | null;
  billable: boolean;
  billingType: string | null;
  billingScope: string | null;
  billingScopeRef: string | null;
  syncTarget: 'WECLAPP' | 'CONNECTWISE' | 'NONE';
  syncStatus: BillingJournalSyncStatus;
  externalRecordId: string | null;
  syncedAt: string | null;
  syncError: string | null;
  handledAt: string | null;
  handledByName: string | null;
  handledNote: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export interface BillingJournalCustomerSummary {
  customerId: string | null;
  customerName: string | null;
  workActualMinutes: number;
  workBookedMinutes: number;
  workBillableBookedMinutes: number;
  travelMinutes: number;
  distanceKm: number;
  materialLines: number;
  waiting: number;
  failed: number;
  manualOpen: number;
}

export interface BillingJournalCounters {
  waiting: number;
  failed: number;
  manualOpen: number;
  oldestWaitingAt: string | null;
}

export interface BillingJournalResponse {
  entries: BillingJournalEntry[];
  truncated: boolean;
  summary: BillingJournalCustomerSummary[];
  counters: BillingJournalCounters;
}

export interface BillingJournalFilter {
  month?: string;
  customerId?: string;
  kind?: BillingJournalKind;
  view?: BillingJournalView;
  q?: string;
}

export interface BillingJournalRetryStats {
  sources: number;
  /** Berichte: eingereiht, Ergebnis kommt im Hintergrund */
  started: number;
  synced: number;
  pending: number;
  failed: number;
  skipped: number;
  unsupported: number;
  moreRemaining: boolean;
}

export interface ControlHubTokenStatus {
  active: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

function query(f: BillingJournalFilter): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function createBillingJournalApi(request: RequestFn) {
  return {
    async getBillingJournal(filter: BillingJournalFilter) {
      return request<BillingJournalResponse>(`/admin/billing-journal${query(filter)}`);
    },

    /** Material/Anfahrt als weiterverarbeitet markieren oder das Häkchen zurücknehmen. */
    async setBillingJournalHandled(ids: string[], handled: boolean, note?: string) {
      return request<{ updated: number }>('/admin/billing-journal/handled', {
        method: 'POST',
        body: JSON.stringify({ ids, handled, ...(note ? { note } : {}) }),
      });
    },

    /** Abgelehnte Posten erneut übertragen — ohne `ids` alle abgelehnten. */
    async retryBillingJournal(ids?: string[]) {
      return request<BillingJournalRetryStats>('/admin/billing-journal/retry', {
        method: 'POST',
        body: JSON.stringify(ids ? { ids } : {}),
      });
    },

    async runBillingJournalSync() {
      return request<{ created: number; updated: number; cancelled: number; errors: number; moreRemaining: boolean }>(
        '/admin/billing-journal/sync',
        { method: 'POST' },
      );
    },

    async downloadBillingJournalCsv(filter: BillingJournalFilter): Promise<void> {
      const res = await fetch(`/api/admin/billing-journal/export.csv${query(filter)}`, { credentials: 'include' });
      if (!res.ok) {
        let msg = 'Export konnte nicht erstellt werden';
        try {
          const j = (await res.json()) as { error?: string };
          msg = j.error ?? msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leistungsjournal-${filter.month ?? filter.view ?? 'alle'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },

    async getControlHubTokenStatus() {
      return request<ControlHubTokenStatus>('/admin/billing-journal/api-token');
    },

    /** Neues Token — der Klartext steht nur in dieser Antwort. */
    async createControlHubToken() {
      return request<ControlHubTokenStatus & { token: string }>('/admin/billing-journal/api-token', { method: 'POST' });
    },

    async revokeControlHubToken() {
      return request<ControlHubTokenStatus>('/admin/billing-journal/api-token', { method: 'DELETE' });
    },
  };
}
